import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  TextInput,
  ActionList,
  Spinner,
  IconButton,
  Avatar,
  Flash,
  Dialog,
  Text,
  Textarea,
} from "@primer/react";
import {
  AlertIcon,
  BookIcon,
  FileBadgeIcon,
  PencilIcon,
  SearchIcon,
  TrashIcon,
  XIcon,
} from "@primer/octicons-react";
import { hinhThucMienGiamApi } from "../../../api/hinhThucMIenGiamApi";
import { ComboboxNamHoc } from "../../../components/combobox-namhoc";
import { useCommonSelectedHook } from "../../../hooks/useCommonSelectedHook";
import { IHocSinh } from "../../../models/response/crm-hocsinh/IHocsinh";
import { NotifyHelper } from "../../../helpers/toast";

interface HinhThucMienGiamProps {
  hocSinh: IHocSinh;
}

interface MienGiamItem {
  id: number;
  ma_hinh_thuc: string;
  hinh_thuc_mien_giam: string;
  nhom_mien_giam: string;
  ghi_chu?: string;
}

interface GroupedMienGiam {
  key: string;
  items: MienGiamItem[];
}

interface ApiResponse<T> {
  is_success: boolean;
  data?: T;
  message?: string;
}

const HinhThucMienGiam: React.FC<HinhThucMienGiamProps> = ({ hocSinh }) => {
  const { nam_hoc, dm_coso_id } = useCommonSelectedHook();

  const [namHocSelected, setNamHocSelected] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const [listMienGiam, setListMienGiam] = useState<MienGiamItem[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [initialSelectedIds, setInitialSelectedIds] = useState<number[]>([]);
  const [isShowDeleteConfirm, setIsShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<number | null>(null);
  const [ghiChuMap, setGhiChuMap] = useState<Record<number, string>>({});

  // State cho dialog sửa ghi chú
  const [isShowGhiChuDialog, setIsShowGhiChuDialog] = useState(false);
  const [editingGhiChuItemId, setEditingGhiChuItemId] = useState<number | null>(null);
  const [editingGhiChuValue, setEditingGhiChuValue] = useState<string>("");
  const [isSavingGhiChu, setIsSavingGhiChu] = useState(false);

  useEffect(() => {
    setNamHocSelected(nam_hoc);
  }, [nam_hoc]);

  useEffect(() => {
    if (namHocSelected && hocSinh.id) {
      getListMienGiamByHocSinh();
      getListMienGiam();
    }
  }, [namHocSelected, hocSinh.id]);

  const getListMienGiam = async () => {
    try {
      const res: ApiResponse<MienGiamItem[]> =
        await hinhThucMienGiamApi.selectAllByNamHoc(
          namHocSelected,
          hocSinh.id,
          dm_coso_id
        );
      if (res.is_success && res.data) {
        setListMienGiam(res.data);
      } else {
        setListMienGiam([]);
      }
    } catch (error) {
      console.error("Error fetching mien giam list:", error);
      setListMienGiam([]);
    }
  };

  const getListMienGiamByHocSinh = async () => {
    try {
      const res: ApiResponse<MienGiamItem[]> =
        await hinhThucMienGiamApi.selectByHocSinh(namHocSelected, hocSinh.id);
      if (res.is_success && res.data) {
        const selectedMienGiamIds = res.data.map((item) => item.id);
        setSelectedIds(selectedMienGiamIds);
        setInitialSelectedIds(selectedMienGiamIds);
        const map: Record<number, string> = {};
        res.data.forEach((item) => {
          if (item.ghi_chu) {
            map[item.id] = item.ghi_chu;
          }
        });
        setGhiChuMap(map);
      }
    } catch (error) {
      console.error("Error fetching selected mien giam:", error);
    }
  };

  const handleCheckboxChange = async (
    itemId: number,
    nhomMienGiam: string,
    checked: boolean
  ) => {
    // Xác định các items trong cùng nhóm với item đang chọn
    const itemsInSameGroup = listMienGiam
      .filter((item) => item.nhom_mien_giam === nhomMienGiam)
      .map((item) => item.id);

    setSelectedIds((prevSelectedIds) => {
      if (checked) {
        // Cập nhật selectedIds để chỉ có item hiện tại được chọn trong nhóm
        return prevSelectedIds
          .filter((id) => !itemsInSameGroup.includes(id)) // Bỏ chọn các item trong cùng nhóm
          .concat(itemId); // Thêm item hiện tại
      } else {
        // Nếu bỏ chọn, xóa item hiện tại khỏi danh sách
        return prevSelectedIds.filter((id) => id !== itemId);
      }
    });

    setError("");

    try {
      if (checked) {
        // Xóa tất cả các item đã chọn trong nhóm
        await hinhThucMienGiamApi.deleteDeXuatMienGiam({
          id: hocSinh.id,
          nam_hoc: namHocSelected,
          sf_hinhthucmiengiam_ids: itemsInSameGroup,
        });

        // Thêm item mới được chọn
        await hinhThucMienGiamApi.insertDeXuatMienGiam({
          id: hocSinh.id,
          nam_hoc: namHocSelected,
          sf_hinhthucmiengiam_ids: [itemId],
        });

        NotifyHelper.Success("Cập nhật thông tin thành công");
      } else {
        // Xóa item đã bỏ chọn
        await hinhThucMienGiamApi.deleteDeXuatMienGiam({
          id: hocSinh.id,
          nam_hoc: namHocSelected,
          sf_hinhthucmiengiam_ids: [itemId],
        });

        NotifyHelper.Success("Xóa mục thành công");
      }
    } catch (error) {
      console.error("Error updating selection:", error);
      NotifyHelper.Error("Có lỗi xảy ra");
    }
  };

  const handleRemoveItem = (itemId: number) => {
    setItemToDelete(itemId); // Gán item cần xóa
    setIsShowDeleteConfirm(true); // Hiển thị Dialog
  };
  const confirmDelete = async () => {
    if (itemToDelete !== null) {
      setSelectedIds((prevSelectedIds) =>
        prevSelectedIds.filter((id) => id !== itemToDelete)
      );

      try {
        await hinhThucMienGiamApi.deleteDeXuatMienGiam({
          id: hocSinh.id,
          nam_hoc: namHocSelected,
          sf_hinhthucmiengiam_ids: [itemToDelete],
        });
        NotifyHelper.Success("Xóa mục thành công");
      } catch (error) {
        console.error("Error deleting item:", error);
        NotifyHelper.Error("Có lỗi xảy ra khi xóa mục");
      } finally {
        setIsShowDeleteConfirm(false); // Đóng Dialog sau khi xóa xong
        setItemToDelete(null); // Đặt lại itemToDelete
      }
    }
  };

  // Mở dialog sửa ghi chú
  const handleEditGhiChu = (itemId: number) => {
    setEditingGhiChuItemId(itemId);
    setEditingGhiChuValue(ghiChuMap[itemId] || "");
    setIsShowGhiChuDialog(true);
  };

  // Lưu ghi chú
  const handleSaveGhiChu = async () => {
    if (editingGhiChuItemId === null) return;
    setIsSavingGhiChu(true);
    try {
      const res = await hinhThucMienGiamApi.updateGhiChuDeXuat({
        ts_hocsinh_id: hocSinh.id,
        sf_hinhthucmiengiam_id: editingGhiChuItemId,
        nam_hoc: namHocSelected,
        ghi_chu: editingGhiChuValue,
      });
      if (res.is_success) {
        setGhiChuMap((prev) => ({
          ...prev,
          [editingGhiChuItemId]: editingGhiChuValue,
        }));
        NotifyHelper.Success("Cập nhật ghi chú thành công");
        setIsShowGhiChuDialog(false);
      } else {
        NotifyHelper.Error(res.message || "Có lỗi xảy ra");
      }
    } catch (error) {
      console.error("Error updating ghi chu:", error);
      NotifyHelper.Error("Có lỗi xảy ra khi cập nhật ghi chú");
    } finally {
      setIsSavingGhiChu(false);
    }
  };

  const groupedMienGiam = useMemo(() => {
    const filtered = listMienGiam.filter((item: MienGiamItem) => {
      const searchLower = searchQuery.toLowerCase();
      return (
        item.ma_hinh_thuc.toLowerCase().includes(searchLower) ||
        item.hinh_thuc_mien_giam.toLowerCase().includes(searchLower)
      );
    });

    return filtered.reduce(
      (groups: { [key: string]: GroupedMienGiam }, current) => {
        const key = current.nhom_mien_giam.toString();
        if (!groups[key]) {
          groups[key] = {
            key,
            items: [],
          };
        }
        groups[key].items.push(current);
        return groups;
      },
      {}
    );
  }, [listMienGiam, searchQuery]);

  const selectedItems = useMemo(() => {
    return listMienGiam.filter((item) => selectedIds.includes(item.id));
  }, [listMienGiam, selectedIds]);

  return (
    <Box sx={{ padding: 0 }}>
      <Box sx={{ display: "flex", gap: 3, marginBottom: 3 }}>
        <FormControl>
          <FormControl.Label>Năm học</FormControl.Label>
          <ComboboxNamHoc
            value={namHocSelected}
            onValueChanged={(value: string) => setNamHocSelected(value)}
          />
        </FormControl>

        <FormControl sx={{ flex: 1 }}>
          <FormControl.Label>Tìm kiếm</FormControl.Label>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextInput
              sx={{ flex: 1 }}
              leadingVisual={SearchIcon}
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearchQuery(e.target.value)
              }
              placeholder="Nhập để tìm kiếm..."
            />
          </Box>
        </FormControl>
      </Box>
      {error && (
        <Flash variant="danger" sx={{ mb: 3 }}>
          {error}
        </Flash>
      )}
      {/* Dialog xác nhận xóa */}
      <Dialog
        isOpen={isShowDeleteConfirm}
        onDismiss={() => setIsShowDeleteConfirm(false)}
      >
        <Dialog.Header>
          <Box display="flex" alignItems="center">
            <div style={{ color: "orange" }}>
              <AlertIcon size={28} className="" />
            </div>

            <Box ml={2}>Lưu ý</Box>
          </Box>
        </Dialog.Header>{" "}
        <Box p={3}>
          <Text>Bạn có chắc chắn muốn xóa mục miễn giảm này?</Text>
          <Box display="flex" mt={3} justifyContent="flex-end">
            <Box mr={2}>
              <Button
                variant="default"
                onClick={() => setIsShowDeleteConfirm(false)}
              >
                Đóng
              </Button>
            </Box>
            <Button variant="danger" onClick={confirmDelete}>
              Xóa
            </Button>
          </Box>
        </Box>
      </Dialog>
      {/* Dialog sửa ghi chú */}
      <Dialog
        isOpen={isShowGhiChuDialog}
        onDismiss={() => setIsShowGhiChuDialog(false)}
      >
        <Dialog.Header>
          <Box display="flex" alignItems="center">
            <Box>Sửa ghi chú</Box>
          </Box>
        </Dialog.Header>
        <Box p={3}>
          <FormControl>
            <FormControl.Label>Ghi chú</FormControl.Label>
            <Textarea
              value={editingGhiChuValue}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setEditingGhiChuValue(e.target.value)
              }
              placeholder="Nhập ghi chú..."
              rows={4}
              sx={{ width: "100%", resize: "vertical" }}
            />
          </FormControl>
          <Box display="flex" mt={3} justifyContent="flex-end" sx={{ gap: 2 }}>
            <Button
              variant="default"
              onClick={() => setIsShowGhiChuDialog(false)}
            >
              Đóng
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveGhiChu}
              disabled={isSavingGhiChu}
            >
              {isSavingGhiChu ? <Spinner size="small" /> : "Lưu"}
            </Button>
          </Box>
        </Box>
      </Dialog>
      <Box sx={{ display: "flex", gap: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Box
            sx={{
              border: "1px solid",
              borderColor: "border.default",
              borderRadius: 2,
              maxHeight: "calc(100vh - 390px)",
              overflowY: "auto",
            }}
          >
            {Object.values(groupedMienGiam).map((group) => (
              <Box key={group.key}>
                <Box
                  sx={{
                    bg: "canvas.subtle",
                    p: 2,
                    borderBottom: "1px solid",
                    borderColor: "border.default",
                    fontWeight: "bold",
                    color: "fg.muted",
                  }}
                >
                  Nhóm miễn giảm: {group.key}
                </Box>
                <ActionList>
                  {group.items.map((item) => (
                    <ActionList.Item
                      key={item.id}
                      style={
                        selectedIds.includes(item.id)
                          ? { opacity: 0.5, pointerEvents: "none" }
                          : {}
                      }
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        <Checkbox
                          checked={selectedIds.includes(item.id)}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            handleCheckboxChange(
                              item.id,
                              item.nhom_mien_giam,
                              e.target.checked
                            );
                          }}
                        />
                        <Box sx={{ display: "flex", flex: 1, ml: 2 }}>
                          <Box sx={{ width: "30%", pr: 2 }}>
                            {item.ma_hinh_thuc}
                          </Box>
                          <Box sx={{ width: "70%" }}>
                            {item.hinh_thuc_mien_giam}
                          </Box>
                        </Box>
                      </Box>
                    </ActionList.Item>
                  ))}
                </ActionList>
              </Box>
            ))}
          </Box>
        </Box>
        <Box sx={{ flex: 1 }} >
          <Box
            sx={{
              border: "1px solid",
              borderColor: "border.default",
              borderRadius: 2,
              maxHeight: "calc(100vh - 420px)",
              overflowY: "auto",
            }}
          >
            <Box
              sx={{
                bg: "canvas.subtle",
                p: 2,
                borderBottom: "1px solid",
                borderColor: "border.default",
                fontWeight: "bold",
                color: "fg.muted",
              }}
            >
              Danh sách đã chọn
            </Box>
            <ActionList showDividers>
              {selectedItems.map((item) => (
                <ActionList.Item key={item.id}>
                  <ActionList.LeadingVisual>
                    <FileBadgeIcon size={16} />
                  </ActionList.LeadingVisual>
                  <Box sx={{ display: "flex", flex: 1, alignItems: "center" }}>
                    <Box
                      sx={{
                        width: "100%",
                        pr: 2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.ma_hinh_thuc}:{" "}
                      <span style={{ fontWeight: "400" }}>
                        {item.hinh_thuc_mien_giam}
                      </span>
                    </Box>
                  </Box>
                  <ActionList.Description variant="block">
                    Nhóm miễn giảm: {item.nhom_mien_giam}
                    {ghiChuMap[item.id] && (
                      <Box as="span" sx={{ display: "block" }}>
                        Ghi chú: {ghiChuMap[item.id]}
                      </Box>
                    )}
                  </ActionList.Description>
                  <ActionList.TrailingVisual>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <IconButton
                        size="small"
                        icon={PencilIcon}
                        variant="default"
                        aria-label="Sửa ghi chú"
                        onClick={() => handleEditGhiChu(item.id)}
                      />
                      <IconButton
                        size="small"
                        className="invisible"
                        icon={TrashIcon}
                        variant="danger"
                        aria-label="Delete"
                        onClick={() => handleRemoveItem(item.id)}
                      />
                    </Box>
                  </ActionList.TrailingVisual>
                </ActionList.Item>
              ))}
            </ActionList>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default HinhThucMienGiam;
